import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const categoryIcons = { Laptop: '💻', Monitor: '🖥️', Phone: '📱', Keyboard: '⌨️', Mouse: '🖱️', Printer: '🖨️', default: '📦' };

const CATEGORY_MAP = {
  Laptop: 'hardware', Monitor: 'hardware', Keyboard: 'hardware', Mouse: 'hardware',
  Printer: 'hardware', Phone: 'hardware', Software: 'software', Network: 'network',
};

const inp = { width: '100%', padding: '0.625rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: theme.white, outline: 'none', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' };

function TicketForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectId = location.state?.preselect_asset_id;

  const [form, setForm] = useState({ title: '', description: '', category: 'hardware', priority: 'medium', asset_id: '' });
  const [myAssets, setMyAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    it.getMyAssets().then((r) => {
      const data = r.data || [];
      setMyAssets(data);
      if (preselectId) {
        const asset = data.find((a) => String(a.asset_id) === String(preselectId));
        if (asset) {
          setSelectedAsset(asset);
          const catName = asset.category || '';
          setForm((f) => ({
            ...f,
            asset_id: String(asset.asset_id),
            title: `Issue with ${asset.name}`,
            category: CATEGORY_MAP[catName] || 'hardware',
          }));
        }
      }
    }).catch(err => console.warn('[HRMS]', err?.message));
  }, [preselectId]);

  const handleAssetChange = (e) => {
    const assetId = e.target.value;
    if (!assetId) {
      setSelectedAsset(null);
      setForm((f) => ({ ...f, asset_id: '', title: '', category: 'hardware' }));
      return;
    }
    const asset = myAssets.find((a) => String(a.asset_id) === String(assetId));
    setSelectedAsset(asset || null);
    if (asset) {
      const catName = asset.category || '';
      setForm((f) => ({
        ...f,
        asset_id: assetId,
        title: f.title || `Issue with ${asset.name}`,
        category: CATEGORY_MAP[catName] || 'hardware',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.asset_id) { setError('Please select an asset to raise a ticket.'); return; }
    if (!form.title || !form.description) { setError('Title and description are required.'); return; }
    setLoading(true); setError('');
    try {
      await it.createTicket({ ...form, asset_id: parseInt(form.asset_id) });
      navigate(-1);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create ticket.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <Button variant="outlineGray" onClick={() => navigate(-1)}>← Back</Button>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Raise New Ticket</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Submit an IT support request for your asset</p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Step 1: Select Asset */}
            <div>
              <label style={{ ...lbl, fontSize: '0.9rem' }}>Select Your Asset *</label>
              <select style={{ ...inp, borderColor: !form.asset_id ? theme.primary : theme.border }} value={form.asset_id} onChange={handleAssetChange}>
                <option value="">— Choose an asset —</option>
                {myAssets.map((a) => (
                  <option key={a.asset_id} value={a.asset_id}>
                    {categoryIcons[a.category] || '📦'} {a.name} {a.asset_tag ? `(${a.asset_tag})` : ''}
                  </option>
                ))}
              </select>
              {myAssets.length === 0 && (
                <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.4rem 0 0' }}>No assets assigned to you. Contact IT admin.</p>
              )}
            </div>

            {/* Asset Info Panel — auto-filled */}
            {selectedAsset && (
              <div style={{ background: theme.pageBg, border: `1px solid ${theme.border}`, borderRadius: '0.625rem', padding: '0.875rem 1rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.625rem' }}>Asset Details</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.75rem' }}>{categoryIcons[selectedAsset.category] || '📦'}</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>{selectedAsset.name}</p>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: theme.textMuted }}>{selectedAsset.make} {selectedAsset.model} · {selectedAsset.category}</p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
                  {selectedAsset.asset_tag && <div style={{ fontSize: '0.78rem', color: theme.text }}><strong>Tag:</strong> {selectedAsset.asset_tag}</div>}
                  {selectedAsset.serial_number && <div style={{ fontSize: '0.78rem', color: theme.text }}><strong>Serial:</strong> {selectedAsset.serial_number}</div>}
                  {selectedAsset.location && <div style={{ fontSize: '0.78rem', color: theme.text }}><strong>Location:</strong> {selectedAsset.location}</div>}
                  {selectedAsset.warranty_expiry && <div style={{ fontSize: '0.78rem', color: theme.text }}><strong>Warranty:</strong> {new Date(selectedAsset.warranty_expiry).toLocaleDateString()}</div>}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label style={lbl}>Issue Title *</label>
              <input style={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brief description of the issue" required />
            </div>

            {/* Description */}
            <div>
              <label style={lbl}>Description *</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '120px' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue in detail — steps to reproduce, error messages, when it started, etc." required />
            </div>

            {/* Category & Priority */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={lbl}>Category</label>
                <select style={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="hardware">Hardware</option>
                  <option value="software">Software</option>
                  <option value="network">Network</option>
                  <option value="email">Email</option>
                  <option value="access">Access / Permissions</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select style={inp} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            {form.priority === 'critical' && (
              <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.875rem', color: theme.danger }}>
                ⚠️ <strong>Critical priority</strong> — This will be escalated immediately to IT management.
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outlineGray" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" variant="primary" loading={loading} disabled={!form.asset_id}>Submit Ticket</Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
}

export default TicketForm;
